/**
 * ScreenEffects — CSS-based fullscreen visual effects
 *
 * - Vignette (dark edges, stronger at night/underground)
 * - Night vision tint (green overlay when potion active)
 * - Low health pulse (red pulsing vignette)
 * - Speed lines (when speed potion active)
 * - Underwater caustics, blur, and pulsing overlay
 */
export class ScreenEffects {
  constructor() {
    this._build();
    this._nightVision = false;
    this._lowHealth = false;
    this._pulsePhase = 0;
    this._underwater = false;
    this._waterPhase = 0;
  }

  _build() {
    // Inject CSS keyframes for underwater caustic animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes screeneffects-caustics {
        0% {
          background-position: 0% 0%;
          opacity: 0.12;
        }
        25% {
          opacity: 0.18;
        }
        50% {
          background-position: 100% 50%;
          opacity: 0.12;
        }
        75% {
          opacity: 0.16;
        }
        100% {
          background-position: 0% 100%;
          opacity: 0.12;
        }
      }
      @keyframes screeneffects-water-pulse {
        0% {
          opacity: 0.25;
          filter: blur(1.5px);
        }
        50% {
          opacity: 0.35;
          filter: blur(2.5px);
        }
        100% {
          opacity: 0.25;
          filter: blur(1.5px);
        }
      }
    `;
    document.head.appendChild(style);

    // Vignette overlay
    this.vignette = document.createElement('div');
    this.vignette.style.cssText = `
      position: fixed; inset: 0; pointer-events: none; z-index: 90;
      background: radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 100%);
      opacity: 0.4;
      transition: opacity 0.5s;
    `;
    document.body.appendChild(this.vignette);

    // Night vision overlay
    this.nightVisionOverlay = document.createElement('div');
    this.nightVisionOverlay.style.cssText = `
      position: fixed; inset: 0; pointer-events: none; z-index: 89;
      background: rgba(30, 200, 80, 0.08);
      opacity: 0;
      transition: opacity 0.3s;
      mix-blend-mode: screen;
    `;
    document.body.appendChild(this.nightVisionOverlay);

    // Low health pulse overlay
    this.lowHealthOverlay = document.createElement('div');
    this.lowHealthOverlay.style.cssText = `
      position: fixed; inset: 0; pointer-events: none; z-index: 88;
      background: radial-gradient(ellipse at center, transparent 40%, rgba(180,0,0,0.3) 100%);
      opacity: 0;
      transition: opacity 0.15s;
    `;
    document.body.appendChild(this.lowHealthOverlay);

    // Underwater tint overlay (blue-green color wash with gentle pulsing)
    this.waterOverlay = document.createElement('div');
    this.waterOverlay.style.cssText = `
      position: fixed; inset: 0; pointer-events: none; z-index: 91;
      background: radial-gradient(ellipse at center, rgba(20, 60, 140, 0.2) 0%, rgba(10, 35, 100, 0.4) 100%);
      opacity: 0;
      transition: opacity 0.4s;
      animation: screeneffects-water-pulse 3s ease-in-out infinite;
      animation-play-state: paused;
    `;
    document.body.appendChild(this.waterOverlay);

    // Underwater caustic pattern overlay (animated light patterns)
    this.causticOverlay = document.createElement('div');
    this.causticOverlay.style.cssText = `
      position: fixed; inset: 0; pointer-events: none; z-index: 92;
      background:
        repeating-conic-gradient(
          from 0deg at 30% 40%,
          rgba(120, 200, 255, 0.06) 0deg,
          transparent 15deg,
          rgba(120, 200, 255, 0.04) 30deg,
          transparent 45deg
        ),
        repeating-conic-gradient(
          from 45deg at 70% 60%,
          rgba(80, 170, 240, 0.05) 0deg,
          transparent 20deg,
          rgba(100, 190, 255, 0.03) 40deg,
          transparent 60deg
        );
      background-size: 200% 200%;
      opacity: 0;
      mix-blend-mode: screen;
      transition: opacity 0.5s;
      animation: screeneffects-caustics 8s linear infinite;
      animation-play-state: paused;
    `;
    document.body.appendChild(this.causticOverlay);
  }

  /**
   * Update screen effects every frame.
   * @param {number} dt - Delta time
   * @param {boolean} isNight - Night time
   * @param {boolean} isUnderground - Player underground
   * @param {boolean} hasNightVision - Night vision potion active
   * @param {number} health - Current health (0-20)
   * @param {boolean} inWater - Player underwater
   */
  update(dt, isNight, isUnderground, hasNightVision, health, inWater) {
    // Vignette intensity
    let vignetteOpacity = 0.3;
    if (isNight) vignetteOpacity = 0.55;
    if (isUnderground) vignetteOpacity = 0.65;
    if (inWater) vignetteOpacity = 0.5;
    if (hasNightVision) vignetteOpacity = 0.15; // Reduce vignette with night vision
    this.vignette.style.opacity = vignetteOpacity.toString();

    // Night vision effect
    if (hasNightVision && !this._nightVision) {
      this.nightVisionOverlay.style.opacity = '1';
      this._nightVision = true;
    } else if (!hasNightVision && this._nightVision) {
      this.nightVisionOverlay.style.opacity = '0';
      this._nightVision = false;
    }

    // Low health pulse
    if (health <= 6 && health > 0) {
      this._pulsePhase += dt * 4;
      const pulse = (Math.sin(this._pulsePhase) + 1) * 0.5; // 0-1
      this.lowHealthOverlay.style.opacity = (0.2 + pulse * 0.3).toString();
      this._lowHealth = true;
    } else if (this._lowHealth) {
      this.lowHealthOverlay.style.opacity = '0';
      this._lowHealth = false;
      this._pulsePhase = 0;
    }

    // Underwater effects: caustic patterns, blur/tint overlay, pulsing
    if (inWater && !this._underwater) {
      // Entering water
      this._underwater = true;
      this.waterOverlay.style.opacity = '1';
      this.waterOverlay.style.animationPlayState = 'running';
      this.causticOverlay.style.opacity = '1';
      this.causticOverlay.style.animationPlayState = 'running';
    } else if (!inWater && this._underwater) {
      // Leaving water
      this._underwater = false;
      this.waterOverlay.style.opacity = '0';
      this.waterOverlay.style.animationPlayState = 'paused';
      this.causticOverlay.style.opacity = '0';
      this.causticOverlay.style.animationPlayState = 'paused';
    }
  }

  /**
   * Play a dimension transition effect (fade to color and back).
   * @param {string} color - CSS color for the fade (e.g. '#000' for black, '#330808' for nether red)
   * @param {number} [duration=1.5] - Total duration in seconds
   * @param {Function} [onMidpoint] - Callback at the midpoint (when screen is fully covered)
   */
  playDimensionTransition(color = '#000000', duration = 1.5, onMidpoint = null, videoSrc = null) {
    if (this._transitionOverlay) return; // already transitioning

    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 9000;
      background: ${color};
      opacity: 0;
      transition: opacity ${duration * 0.4}s ease-in;
      pointer-events: none;
    `;

    // If video source provided, add a background video
    let video = null;
    if (videoSrc) {
      video = document.createElement('video');
      video.style.cssText = `
        position: absolute; inset: 0;
        width: 100%; height: 100%;
        object-fit: cover;
        pointer-events: none;
      `;
      video.muted = true;
      video.playsInline = true;
      video.src = videoSrc;
      overlay.appendChild(video);
    }

    document.body.appendChild(overlay);
    this._transitionOverlay = overlay;

    // Fade in
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      if (video) video.play().catch(() => {});
    });

    // Midpoint: execute callback (dimension switch happens here)
    setTimeout(() => {
      if (onMidpoint) onMidpoint();
      // Fade out
      overlay.style.transition = `opacity ${duration * 0.6}s ease-out`;
      overlay.style.opacity = '0';
    }, duration * 400);

    // Cleanup
    setTimeout(() => {
      if (video) { video.pause(); video.src = ''; }
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      this._transitionOverlay = null;
    }, duration * 1000);
  }
}
